import Select from "react-select";
import { useRouter } from "next/router";
import subjectsAndLevels from "../../util/data/subjectsAndLevels.json";
import { SubjectsAndLevels } from "../../models/SubjectsAndLevels";
import urlSlugController from "../../util/urlSlugController";
import urlParameterController from "../../util/urlParameterController";
import {
  subjectPickerDropdownStyles,
  customStyles,
} from "../../util/sharedStyles";

const filterBytype = (
  filter: string,
  subjectsAndLevels: Array<SubjectsAndLevels>
) => {
  let values: Array<object> = [];
  // Map all objects in subjectsAndLevels with matching type to filter
  values = subjectsAndLevels
    .filter((options) => options.type === filter)
    .map((value) => ({
      label: value.key,
      value: value.urlKey,
    }));
  return values;
};

const filterByUrlKey = (
  oppositeFilterValue: string,
  subjectsAndLevels: Array<SubjectsAndLevels>
) => {
  let values: Array<object> = [];
  // Map values in the object in subjectsAndLevels with matching urlKey to oppositeFilterValue
  values = subjectsAndLevels
    .filter((options) => options.urlKey === oppositeFilterValue)
    .flatMap((levelOrSubject) =>
      levelOrSubject.values.flatMap((values) => ({
        label: values.value,
        value: values.urlValue,
      }))
    );
  return values;
};

const oppositeFilterValueDefined = (oppositeFilterValue: string) => {
  return oppositeFilterValue;
};

const oppositeFilterValueUndefinedOrNotFound = (
  oppositeFilterValue: string,
  filteredOptions: Array<object>
) => {
  return !oppositeFilterValue || filteredOptions.length === 0;
};

// Edit featured/popular subjects here
const popularSubjects = [
  { label: "Maths", value: "Maths" },
  { label: "English", value: "English" },
  { label: "Biology", value: "Biology" },
  { label: "Chemistry", value: "Chemistry" },
  { label: "Physics", value: "Physics" },
];

interface FilterOptions {
  label?: string;
  value?: string;
  options?: Array<FilterOptions>;
}

const getFilterValues = (
  filter: string,
  subjectAndLevel: object,
  subjectsAndLevels: Array<SubjectsAndLevels>
) => {
  let filteredOptions: Array<FilterOptions>;
  const oppositeFilter = filter === "subject" ? "level" : "subject";
  // If opposite filter is set look for values in subjectsAndLevels
  if (oppositeFilterValueDefined(subjectAndLevel[oppositeFilter])) {
    filteredOptions = filterByUrlKey(
      subjectAndLevel[oppositeFilter],
      subjectsAndLevels
    );
  }
  // If opposite filter is not set or could not be found in subjectsAndLevels
  if (
    oppositeFilterValueUndefinedOrNotFound(
      subjectAndLevel[oppositeFilter],
      filteredOptions
    )
  ) {
    filteredOptions = filterBytype(filter, subjectsAndLevels);
  }

  if (filter === "subject") {
    // filter out popular subjects not matching levels
    const popularSubjectsFiltered = [];

    for (const option of popularSubjects) {
      if (filteredOptions.find((o) => o.value === option.value)) {
        popularSubjectsFiltered.push(option);
      }
    }

    filteredOptions = [
      {
        label: "Popular Subjects",
        options: popularSubjectsFiltered,
      },
      {
        label: "All Subjects",
        options: filteredOptions,
      },
    ];
  }

  filteredOptions.unshift({
    label: `All ${filter}s`,
    value: "",
  });

  return filteredOptions;
};

const getSelectedFilterValue = (
  filter: string,
  subjectAndLevel: object,
  subjectsAndLevels: Array<SubjectsAndLevels>
) => {
  const value = subjectAndLevel[filter];
  const allFilterOption = {
    label: `All ${filter}s`,
    value: "",
  };
  if (!value) {
    return allFilterOption;
  }
  const filterOption: object = subjectsAndLevels
    .filter((options) => options.urlKey === value)
    .map((value) => ({
      label: value.key,
      value: value.urlKey,
    }))[0];
  if (!filterOption) {
    return allFilterOption;
  } else {
    return filterOption;
  }
};

const DropdownIndicator = () => (
  // eslint-disable-next-line @next/next/no-img-element
  <img
    className="dropdown-image"
    src="/view-tutors/chevron-vertical-primary7.svg"
    alt="Dropdown icon"
  />
);

const SubjectLevelPickerDropdown = ({ filter, subjectAndLevel }) => {
  const router = useRouter();
  const ariaLabel = "Select " + filter;
  const placeHolder = "All " + filter + "s";
  const sortedOptions = getFilterValues(
    filter,
    subjectAndLevel,
    subjectsAndLevels
  );
  const defaultOption = getSelectedFilterValue(
    filter,
    subjectAndLevel,
    subjectsAndLevels
  );
  return (
    <Select
      instanceId="subject-picker"
      css={subjectPickerDropdownStyles}
      styles={customStyles}
      components={{ DropdownIndicator }}
      options={sortedOptions}
      defaultValue={defaultOption}
      aria-label={ariaLabel}
      placeholder={placeHolder}
      onChange={(newValue: { label: string; value: string }) => {
        const sanitisedLabel = newValue.value
          .toLowerCase()
          .replaceAll("-", "_");
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "GAevent",
          event_id: "14",
          eventCategory: "filter",
          eventAction: filter,
          eventLabel: sanitisedLabel,
        });
        const path = router.asPath;
        const slugs = subjectAndLevel;
        slugs[filter] = newValue.value;
        let newPath = urlSlugController(slugs, path);
        newPath = urlParameterController("PAGINATION", "1", newPath); //Reset pagination to page=1
        router.push(newPath);
      }}
      menuPlacement="bottom"
    />
  );
};

export default SubjectLevelPickerDropdown;
